import { useEffect, useRef, useState } from "react"
import { Link, useNavigate, useSearchParams } from "react-router-dom"
import {
  getAskDynamoSession,
  getSampleThoughts,
  postAskDynamoMessage,
  AskDynamoApiError,
  type AskDynamoMessage,
  type AskDynamoSessionResponse,
  type SampleThought,
} from "~/api/askDynamo"
import { AskDynamoComposer } from "~/components/AskDynamoComposer"
import { AskDynamoDogPill } from "~/components/AskDynamoDogPill"
import { AskDynamoFollowUpSuggestions } from "~/components/AskDynamoFollowUpSuggestions"
import { AskDynamoHeader } from "~/components/AskDynamoHeader"
import { AskDynamoJumpToLatestButton } from "~/components/AskDynamoJumpToLatestButton"
import { AskDynamoMessageList } from "~/components/AskDynamoMessageList"
import { AskDynamoPawIllustration } from "~/components/AskDynamoPawIllustration"
import { AskDynamoSuggestionList } from "~/components/AskDynamoSuggestionList"
import { AskDynamoThoughtsPreferenceCard } from "~/components/AskDynamoThoughtsPreferenceCard"
import { LoggedInScreenCard } from "~/components/LoggedInScreenCard"
import { PageShell } from "~/components/PageShell"
import { SafetyDisclaimer } from "~/components/SafetyDisclaimer"
import { useAtBottom } from "~/hooks/useAtBottom"
import { trackEvent } from "~/utils/trackEvent"

export function AskDynamoPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [session, setSession] = useState<AskDynamoSessionResponse | null>(null)
  const [messages, setMessages] = useState<AskDynamoMessage[]>([])
  const [composerValue, setComposerValue] = useState("")
  const [followUpQuestions, setFollowUpQuestions] = useState<SampleThought[]>([])
  const [loading, setLoading] = useState(true)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const composerRef = useRef<HTMLTextAreaElement>(null)
  const lastShownSequenceNumbers = useRef<number[]>([])
  const { bottomRef, isAtBottom, scrollToBottom } = useAtBottom()
  const wasAtBottomRef = useRef(true)
  const hydratedRef = useRef(false)

  useEffect(() => {
    getAskDynamoSession()
      .then((data) => {
        setSession(data)
        setMessages(data.messages)
        const prefill = searchParams.get("q")
        if (prefill) setComposerValue(prefill)
      })
      .catch(() => navigate("/login", { replace: true }))
      .finally(() => setLoading(false))
  }, [navigate, searchParams])

  useEffect(() => {
    wasAtBottomRef.current = isAtBottom
  }, [isAtBottom])

  useEffect(() => {
    if (!loading && session && !hydratedRef.current) {
      hydratedRef.current = true
      wasAtBottomRef.current = isAtBottom
      return
    }
    if (!hydratedRef.current) return
    if (!wasAtBottomRef.current) return
    scrollToBottom("instant")
  }, [messages, sending, scrollToBottom, loading, session, isAtBottom])

  const dogName = session?.dog.name ?? "your dog"
  const dailyRemaining = session?.usage.daily_remaining ?? 0
  const limitReached = dailyRemaining === 0
  const showLanding = messages.length === 0 && !sending
  const showFollowUp = messages.some((message) => message.role === "assistant") && !sending

  useEffect(() => {
    if (!showFollowUp) {
      setFollowUpQuestions([])
      return
    }

    let cancelled = false

    getSampleThoughts(3, lastShownSequenceNumbers.current)
      .then((thoughts) => {
        if (cancelled) return
        setFollowUpQuestions(thoughts)
        lastShownSequenceNumbers.current = thoughts.map((thought) => thought.sequence_number)
      })
      .catch(() => {
        if (!cancelled) setFollowUpQuestions([])
      })

    return () => {
      cancelled = true
    }
  }, [showFollowUp, messages])

  async function handleSend(content: string) {
    const trimmed = content.trim()
    if (!trimmed || sending || limitReached) return

    setError(null)
    setSending(true)
    setComposerValue("")

    try {
      const result = await postAskDynamoMessage(trimmed)
      setMessages(result.messages)
      setSession((prev) =>
        prev ? { ...prev, usage: result.usage, messages: result.messages } : prev,
      )
      trackEvent("ask_dynamo_message_sent", { dogName })
    } catch (err) {
      if (err instanceof AskDynamoApiError && err.dailyLimitExceeded) {
        setSession((prev) =>
          prev
            ? {
                ...prev,
                usage: { ...prev.usage, daily_remaining: 0 },
              }
            : prev,
        )
        setError("Daily question limit reached")
      } else {
        const message =
          err instanceof AskDynamoApiError
            ? err.message
            : "Could not send your question. Please try again."
        setError(message)
        setComposerValue(trimmed)
      }
    } finally {
      setSending(false)
    }
  }

  function handleSuggestionSelect(text: string) {
    setComposerValue(text)
  }

  function handleFollowUpSelect(text: string) {
    setComposerValue(text)
    composerRef.current?.focus()
  }

  function handleJumpToLatest() {
    scrollToBottom("smooth")
    trackEvent("ask_dynamo_jump_to_latest", { dogName })
  }

  if (loading) {
    return (
      <PageShell>
        <p className="text-[var(--color-text-secondary)]">Loading...</p>
      </PageShell>
    )
  }

  if (!session) return null

  return (
    <PageShell innerClassName="max-w-lg w-full" fixedTabNav>
      <LoggedInScreenCard>
        <AskDynamoHeader />

        <AskDynamoDogPill
          dogName={dogName}
          archetypeLabel={session.dog.archetype_label ?? "Archetype"}
          archetypeIcon={session.dog.archetype_icon}
          portrait={session.dog.portrait}
        />

        {showLanding && (
          <section className="mt-6 text-center" aria-labelledby="ask-dynamo-hero-heading">
            <AskDynamoPawIllustration className="mx-auto h-20 w-20" />
            <h2
              id="ask-dynamo-hero-heading"
              className="mt-4 text-xl font-semibold text-[var(--color-text-primary)]"
            >
              What&apos;s on your mind about {dogName} today?
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-[var(--color-text-secondary)]">
              Ask a question about behaviour, training, or daily life — tailored to {dogName}&apos;s
              unique personality.
            </p>
          </section>
        )}

        <div className="relative">
          <AskDynamoMessageList messages={messages} sending={sending} />
          <AskDynamoJumpToLatestButton
            visible={(messages.length > 0 || sending) && !isAtBottom}
            onClick={handleJumpToLatest}
          />
        </div>

        <div ref={bottomRef} aria-hidden className="h-px" />

        <AskDynamoComposer
          ref={composerRef}
          dogName={dogName}
          value={composerValue}
          onChange={setComposerValue}
          onSubmit={() => void handleSend(composerValue)}
          disabled={limitReached}
          sending={sending}
        />

        {error && (
          <p className="mt-2 text-sm text-red-700" role="alert">
            {error}
          </p>
        )}

        {showFollowUp && (
          <AskDynamoFollowUpSuggestions
            questions={followUpQuestions.map((thought) => thought.text)}
            onSelect={handleFollowUpSelect}
            disabled={limitReached || sending}
          />
        )}

        <p className="mt-3 text-center text-sm text-[var(--color-text-secondary)]">
          You have{" "}
          <strong className="text-[var(--color-ask-dynamo)]">{dailyRemaining}</strong> free
          question{dailyRemaining === 1 ? "" : "s"} remaining today
        </p>

        {showLanding && session.thought_preference && (
          <AskDynamoThoughtsPreferenceCard
            initialFrequency={session.thought_preference.frequency}
            onboardingCompleted={!!session.thought_preference.onboarding_completed_at}
            onSaved={(frequency, onboardingCompleted) => {
              setSession((prev) =>
                prev && prev.thought_preference
                  ? {
                      ...prev,
                      thought_preference: {
                        ...prev.thought_preference,
                        frequency,
                        enabled: frequency !== "off",
                        onboarding_completed_at: onboardingCompleted
                          ? new Date().toISOString()
                          : prev.thought_preference.onboarding_completed_at,
                      },
                    }
                  : prev,
              )
            }}
          />
        )}

        <p className="mt-2 text-center text-sm">
          <Link
            to="/deeper-insights"
            className="font-medium text-[var(--color-ask-dynamo-muted)] underline"
          >
            See what&apos;s coming next for Doggy Dynamo
          </Link>
        </p>

        {showLanding && (
          <AskDynamoSuggestionList
            dogName={dogName}
            suggestions={session.suggestions}
            onSelect={handleSuggestionSelect}
            disabled={limitReached || sending}
          />
        )}

        <SafetyDisclaimer />
      </LoggedInScreenCard>
    </PageShell>
  )
}
